import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authService } from '../services/api'

const AdminAuthContext = createContext()

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(() => localStorage.getItem('tonys-admin-token'))

  const loadUser = useCallback(async () => {
    if (!token) { setLoading(false); return }
    try {
      const data = await authService.me()
      // Only allow admin/staff roles
      if (data.role !== 'admin' && data.role !== 'staff') {
        localStorage.removeItem('tonys-admin-token')
        setToken(null)
        setUser(null)
      } else {
        setUser(data)
      }
    } catch {
      localStorage.removeItem('tonys-admin-token')
      setToken(null)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadUser() }, [loadUser])

  useEffect(() => {
    const handleLogout = () => { setToken(null); setUser(null) }
    window.addEventListener('admin:logout', handleLogout)
    return () => window.removeEventListener('admin:logout', handleLogout)
  }, [])

  const login = async (username, password) => {
    const data = await authService.login(username, password)
    // If server requires 2FA, return a signal object — do NOT store a token
    if (data.requires_2fa) {
      return { requires2fa: true }
    }
    localStorage.setItem('tonys-admin-token', data.access_token)
    setToken(data.access_token)
    const userData = await authService.me()
    if (userData.role !== 'admin' && userData.role !== 'staff') {
      localStorage.removeItem('tonys-admin-token')
      setToken(null)
      throw new Error('Access denied. Admin privileges required.')
    }
    setUser(userData)
    return { requires2fa: false, user: userData }
  }

  // Used after 2FA verification — token already obtained, just store it
  const loginWithToken = async (access_token) => {
    localStorage.setItem('tonys-admin-token', access_token)
    setToken(access_token)
    const userData = await authService.me()
    if (userData.role !== 'admin' && userData.role !== 'staff') {
      localStorage.removeItem('tonys-admin-token')
      setToken(null)
      throw new Error('Access denied. Admin privileges required.')
    }
    setUser(userData)
    return userData
  }

  const logout = () => {
    localStorage.removeItem('tonys-admin-token')
    setToken(null)
    setUser(null)
  }

  return (
    <AdminAuthContext.Provider value={{
      user, loading, token, login, loginWithToken, logout,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
    }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}
